import { lazy, Suspense } from "react";
import SEOHead from "@/components/SEOHead";
const Navbar = lazy(() => import("@/components/Navbar"));
import Hero from "@/components/Hero";
const Footer = lazy(() => import("@/components/Footer"));
import ScrollToTop from "@/components/ScrollToTop";

// Lazy-load below-the-fold sections to reduce main-thread work on initial load
const TrendingTopics = lazy(() => import("@/components/TrendingTopics"));
const CaseStudyCarousel = lazy(() => import("@/components/CaseStudyCarousel"));
const WhyQbD = lazy(() => import("@/components/WhyQbD"));
const Industries = lazy(() => import("@/components/Industries"));
const AboutTeaser = lazy(() => import("@/components/AboutTeaser"));
const Solutions = lazy(() => import("@/components/Solutions"));
const ServicesGrid = lazy(() => import("@/components/ServicesGrid"));
const GatewayCTA = lazy(() => import("@/components/GatewayCTA"));
const LatestNews = lazy(() => import("@/components/LatestNews"));
const KnowledgeCenter = lazy(() => import("@/components/KnowledgeCenter"));
const ContactCTA = lazy(() => import("@/components/ContactCTA"));
const VlmCareHomeBanner = lazy(() => import("@/components/VlmCareHomeBanner"));
import { useLanguage } from "@/hooks/useLanguage";

const seoByLang = {
  en: {
    title: "QbD Group — Life Sciences Consulting | 700+ Experts",
    description: "QbD Group accelerates life sciences innovation with 700+ experts in regulatory affairs, quality assurance, clinical trials & compliance.",
  },
  es: {
    title: "QbD Group — Consultoría Life Sciences | 700+ Expertos",
    description: "QbD Group acelera la innovación en Life Sciences con 700+ expertos. Regulatory affairs, Quality Assurance, ensayos clínicos y consultoría.",
  },
  "zh-cn": {
    title: "QbD Group — 生命科学咨询 | 700+专家",
    description: "QbD Group拥有700多位专家，加速生命科学创新。提供法规事务、质量保证、临床试验及合规咨询，从创意到患者。",
  },
};

const organizationJsonLd = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "@id": "https://qbdgroup.com/#organization",
  name: "QbD Group",
  url: "https://qbdgroup.com",
  logo: {
    "@type": "ImageObject",
    url: "https://qbdgroup.com/assets/qbd-logo.webp",
    width: 280,
    height: 60,
  },
  description: "Life sciences consulting company with 700+ experts across regulatory affairs, quality assurance, clinical trials, pharmacovigilance, regulatory toxicology, lab services, and software compliance.",
  foundingDate: "2012",
  foundingLocation: { "@type": "Place", address: { "@type": "PostalAddress", addressCountry: "BE" } },
  numberOfEmployees: { "@type": "QuantitativeValue", minValue: 600 },
  sameAs: [
    "https://www.linkedin.com/company/qbd-group/",
    "https://www.youtube.com/@QbDGroup",
  ],
  contactPoint: {
    "@type": "ContactPoint",
    contactType: "sales",
    url: "https://qbdgroup.com/en/contact",
    availableLanguage: ["English", "Spanish", "Chinese"],
  },
  areaServed: ["Europe", "United States", "Asia"],
  knowsAbout: [
    "Regulatory Affairs", "Quality Assurance", "Clinical Trials",
    "Pharmacovigilance", "Regulatory Toxicology", "Lab Services",
    "Software Compliance", "Medical Devices", "IVD", "Pharma", "Biotech",
  ],
};

const websiteJsonLd = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": "https://qbdgroup.com/#website",
  name: "QbD Group",
  url: "https://qbdgroup.com",
  publisher: { "@id": "https://qbdgroup.com/#organization" },
  inLanguage: ["en", "es", "zh-Hans"],
};

const siteNavigationJsonLd = {
  "@context": "https://schema.org",
  "@type": "SiteNavigationElement",
  name: [
    "Solutions", "Services", "Industries",
    "About Us", "Knowledge Center", "Events", "Contact",
  ],
  url: [
    "https://qbdgroup.com/en/solutions",
    "https://qbdgroup.com/en/services",
    "https://qbdgroup.com/en/industries",
    "https://qbdgroup.com/en/about-qbd",
    "https://qbdgroup.com/en/knowledge-center",
    "https://qbdgroup.com/en/events",
    "https://qbdgroup.com/en/contact",
  ],
};

const Index = () => {
  const lang = useLanguage();
  const seo = seoByLang[lang];

  return (
    <div className="min-h-screen relative">
      <SEOHead
        title={seo.title}
        description={seo.description}
        ogImage="https://qbdgroup.com/og-image.png"
      >
        <script type="application/ld+json">{JSON.stringify(organizationJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(websiteJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(siteNavigationJsonLd)}</script>
      </SEOHead>
      <ScrollToTop />
      <Suspense fallback={null}><Navbar /></Suspense>
      <Hero />
      <Suspense fallback={null}>
        
        <AboutTeaser />
        <Industries />
        <Solutions />
        <CaseStudyCarousel />
        <ServicesGrid />
        <WhyQbD />
        <VlmCareHomeBanner />
        <GatewayCTA />
        <LatestNews />
        <KnowledgeCenter />
        <ContactCTA />
      </Suspense>
      <Suspense fallback={null}><Footer /></Suspense>
    </div>
  );
};

export default Index;
